import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { LoadingCard, LoadingSpinner } from "../components/LoadingSpinner";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import AddressAutocomplete from "../components/AddressAutocomplete";
import PhoneInput from "../components/ui/PhoneInput";
import { formatPhone } from "../utils/phoneUtils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Checkbox } from "../components/ui/checkbox";
import { DateTimePicker } from "../components/ui/time-picker";
import { formatActivityTimePST } from "../utils/dateUtils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Referral source options
const REFERRAL_SOURCE_OPTIONS = [
  "Yelp",
  "Google", 
  "Website",
  "Self Generated",
  "Facebook",
  "Word of Mouth", 
  "Other",
  "Yard Sign",
  "Company Vehicles",
  "Previous Customer",
  "Instagram", 
  "Facebook/Instagram Ad",
  "Other Contractor",
  "Previous Customer Referral"
];

// Status options
const STATUS_OPTIONS = [
  "New",
  "Contacting",
  "Qualified", 
  "Unqualified",
  "Postponed",
  "Chose Different Company",
  "Design Only",
  "Contract",
  "Design and Project",
  "Contact Later",
  "Cancelled",
  "VIP",
  "Converted"
];
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Plus,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Calendar,
  Filter,
  Search,
  Users,
  Edit,
  UserPlus,
  Trash2,
  Eye,
  MessageSquare,
  Pencil,
  AlertTriangle,
  User,
  Building,
  FileText,
  X,
  ChevronDown,
} from "lucide-react";
import { leadService } from "../services/leadService";
import { emailService } from "../services/emailService";
import { emailTemplateService } from "../services/emailTemplateService";
import { designerService } from "../services/designerService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../contexts/AuthContext";

export default function Leads() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Check if user has edit permissions (not a basic 'user' role)
  const canEdit = user?.role !== 'user';
  const canDelete = user?.role === 'admin';
  
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [viewingLead, setViewingLead] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const leadsPerPage = 25;
  
  // Send Email states
  const [isSendEmailDialogOpen, setIsSendEmailDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(null);
  const [emailData, setEmailData] = useState({
    template_id: "",
    to_email: "",
    to_name: "", 
    subject: "",
    body: "",
    variables: {}
  });
  
  const [newLead, setNewLead] = useState({
    name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zip_code: "",
    service_type: "",
    project_value: "",
    budget: "",
    notes: "",
    description: "",
    source: "",
    referral_source: "",
    priority: "medium",
    status: "New",
    assigned_to: "",
    company_account: "",
    company: "",
    spouse: "",
    spouse_phone: "",
    spouse_email: "",
    hoa: false,
    gate_code: "",
    first_email_date: "",
    first_call_date: "",
    appointment_date: ""
  });
  
  // Contact log state
  const [isContactLogDialogOpen, setIsContactLogDialogOpen] = useState(false);
  const [newContactLog, setNewContactLog] = useState({
    contact_type: "phone",
    contact_method: "outbound",
    summary: ""
  });
  const [editingContactLog, setEditingContactLog] = useState(null);
  const [editingContactLogIndex, setEditingContactLogIndex] = useState(null);
  const [deletingContactLogIndex, setDeletingContactLogIndex] = useState(null);

  // API hooks
  const { data: leadsData, isLoading: loading } = useQuery({
    queryKey: ['leads', filterStatus, searchTerm, currentPage],
    queryFn: () => leadService.getAll({ 
      status: filterStatus === "all" ? undefined : filterStatus,
      search: searchTerm || undefined,
      page: currentPage,
      limit: leadsPerPage
    })
  });

  const { data: designersData } = useQuery({
    queryKey: ['designers'],
    queryFn: () => designerService.getAll()
  });

  const location = useLocation();

  // Handle opening lead from search (via query parameter)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get('openId');
    
    if (openId && leadsData?.data) {
      const lead = leadsData.data.find(l => l.id === openId);
      if (lead) {
        setViewingLead(lead);
        setIsViewDialogOpen(true);
        // Clear the query parameter from URL
        window.history.replaceState({}, '', '/leads');
      }
    }
  }, [location.search, leadsData]);

  // Mutations
  const createLeadMutation = useMutation({
    mutationFn: (data) => leadService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({ description: "Lead created successfully!" });
      setNewLead({
        name: "",
        last_name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        zip_code: "",
        service_type: "",
        project_value: "",
        budget: "",
        notes: "",
        description: "",
        source: "",
        referral_source: "",
        priority: "medium",
        status: "New",
        assigned_to: "",
        company_account: "",
        spouse: "",
        spouse_phone: "",
        spouse_email: "",
        hoa: false,
        gate_code: "",
        first_email_date: "",
        first_call_date: "",
        appointment_date: ""
      });
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to create lead"
      });
    }
  });

  const updateLeadMutation = useMutation({
    mutationFn: ({ id, data }) => leadService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({ description: "Lead updated successfully!" });
      setIsEditDialogOpen(false);
      setEditingLead(null);
    },
    onError: (error) => {
      // Extract error message safely
      let errorMessage = "Failed to update lead";
      if (error?.detail) {
        errorMessage = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        variant: "destructive",
        description: errorMessage
      });
    }
  });

  const convertToCustomerMutation = useMutation({
    mutationFn: (leadId) => leadService.convertToCustomer(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({ description: "Lead converted to customer successfully!" });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to convert lead"
      });
    }
  });

  const sendEmailMutation = useMutation({
    mutationFn: (data) => emailService.send(data),
    onSuccess: async (response) => {
      // Invalidate leads query to refresh contact_logs with the new email activity
      queryClient.invalidateQueries({ queryKey: ['leads'] });

      // If viewing lead dialog is open, refresh the viewing lead data to show new email activity
      if (viewingLead && isViewDialogOpen) {
        try {
          const resp = await leadService.getById(viewingLead.id || viewingLead._id);
          setViewingLead(resp.data);
        } catch (error) {
          console.error("Failed to refresh viewing lead after email:", error);
        }
      }

      const warnings = response?.data?.warnings || response?.warnings || [];
      if (warnings.length > 0) {
        toast({ description: `Email sent! Warning: ${warnings.join('; ')}`, duration: 8000 });
      } else {
        toast({ description: "Email sent successfully!" });
      }
      setIsSendEmailDialogOpen(false);
      setSelectedEmailTemplate(null);
      setEmailData({
        template_id: "",
        to_email: "",
        to_name: "",
        subject: "",
        body: "",
        variables: {}
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to send email"
      });
    }
  });

  const addContactLogMutation = useMutation({
    mutationFn: ({ leadId, contactLog }) => leadService.addContactLog(leadId, contactLog),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({ description: "Contact log added successfully!" });
      setIsContactLogDialogOpen(false);
      setNewContactLog({
        contact_type: "phone",
        contact_method: "outbound",
        summary: ""
      });
      // Refresh viewing lead data
      if (viewingLead) {
        leadService.getById(viewingLead.id || viewingLead._id).then(res => {
          setViewingLead(res.data?.data || res.data);
        });
      }
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to add contact log"
      });
    }
  });

  const updateContactLogMutation = useMutation({
    mutationFn: ({ leadId, logIndex, contactLog }) => leadService.updateContactLog(leadId, logIndex, contactLog),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({ description: "Contact log updated successfully!" });
      setEditingContactLog(null);
      setEditingContactLogIndex(null);
      // Refresh viewing lead data
      if (viewingLead) {
        leadService.getById(viewingLead.id || viewingLead._id).then(res => {
          setViewingLead(res.data?.data || res.data);
        });
      }
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to update contact log"
      });
    }
  });

  const deleteContactLogMutation = useMutation({
    mutationFn: ({ leadId, logIndex }) => leadService.deleteContactLog(leadId, logIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({ description: "Contact log deleted successfully!" });
      // Refresh viewing lead data
      if (viewingLead) {
        leadService.getById(viewingLead.id || viewingLead._id).then(res => {
          setViewingLead(res.data?.data || res.data);
        });
      }
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to delete contact log"
      });
    }
  });

  // Load email templates for leads
  const { data: emailTemplatesData } = useQuery({
    queryKey: ['email-templates', 'lead'],
    queryFn: () => emailTemplateService.getAll({
      category: 'lead',
      active_only: true
    })
  });

  const getStatusColor = (status) => {
    if (!status) return "bg-gray-500";
    // Generate colors based on status
    switch (status.toLowerCase()) {
      case "new": return "bg-blue-500";
      case "contacting": return "bg-yellow-500";
      case "qualified": return "bg-green-500";
      case "unqualified": return "bg-red-500";
      case "postponed": return "bg-gray-500";
      case "chose different company": return "bg-red-600";
      case "design only": return "bg-purple-500";
      case "contract": return "bg-brand-600";
      case "design and project": return "bg-indigo-500";
      case "contact later": return "bg-orange-500";
      case "cancelled": return "bg-red-700";
      case "vip": return "bg-amber-500";
      default: return "bg-gray-500";
    }
  };

  const getStatusLabel = (status) => {
    return status || "Unknown";
  };

  const leads = leadsData?.data || [];

  const handleAddLead = async () => {
    // Clean up data before sending - convert empty strings to appropriate types
    const leadData = {
      ...newLead,
      // Convert empty strings to undefined for optional fields
      project_value: newLead.project_value ? parseFloat(newLead.project_value) : 0,
      budget: newLead.budget || undefined,
      // Convert empty strings to undefined for dates
      first_email_date: newLead.first_email_date || undefined,
      first_call_date: newLead.first_call_date || undefined,
      appointment_date: newLead.appointment_date || undefined,
      // Clean up optional string fields
      email: newLead.email || undefined,
      phone: newLead.phone || undefined,
      address: newLead.address || undefined,
      city: newLead.city || undefined,
      zip_code: newLead.zip_code || undefined,
      service_type: newLead.service_type || undefined,
      source: newLead.source || undefined,
      referral_source: newLead.referral_source || undefined,
      notes: newLead.notes || undefined,
      description: newLead.description || undefined,
      company_account: newLead.company_account || undefined,
      spouse: newLead.spouse || undefined,
      spouse_phone: newLead.spouse_phone || undefined,
      spouse_email: newLead.spouse_email || undefined,
      gate_code: newLead.gate_code || undefined,
      last_name: newLead.last_name || undefined,
      assigned_to: newLead.assigned_to || undefined,
      // Include status and priority - ensure they are always sent
      status: newLead.status,
      priority: newLead.priority
    };
    
    console.log('🔍 Creating lead with data:', leadData);
    console.log('🔍 Status being sent:', leadData.status);
    
    createLeadMutation.mutate(leadData);
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await leadService.updateStatus(leadId, newStatus);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    } catch (error) {
      console.error("Failed to update lead status:", error);
    }
  };

  const handleEditLead = (lead) => {
    // Format datetime fields for datetime-local inputs (format: YYYY-MM-DDTHH:MM)
    const formatDateTimeLocal = (dateString) => {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        // Convert to local timezone and format as YYYY-MM-DDTHH:MM
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch (e) {
        return "";
      }
    };

    // Format date fields for date inputs (format: YYYY-MM-DD)
    const formatDate = (dateString) => {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (e) {
        return "";
      }
    };

    // Sanitize lead object to prevent error objects from being rendered
    const sanitizeLead = (leadData) => {
      const sanitized = {};
      for (const [key, value] of Object.entries(leadData)) {
        // Check if value is a Zod error object
        if (value && typeof value === 'object' && !Array.isArray(value) && (value.type || value.msg || value.loc)) {
          console.warn(`Skipping invalid field ${key}:`, value);
          sanitized[key] = "";
        } 
        // Check if array contains error objects
        else if (Array.isArray(value)) {
          sanitized[key] = value.filter(item => {
            // Filter out error objects from arrays
            if (item && typeof item === 'object' && (item.type || item.msg || item.loc)) {
              console.warn(`Removing error object from array ${key}:`, item);
              return false;
            }
            return true;
          });
        } 
        else {
          sanitized[key] = value;
        }
      }
      return sanitized;
    };

    const sanitizedLead = sanitizeLead(lead);

    const finalLead = {
      ...sanitizedLead,
      project_value: sanitizedLead.project_value || 0,
      appointment_date: formatDateTimeLocal(sanitizedLead.appointment_date),
      first_email_date: formatDate(sanitizedLead.first_email_date),
      first_call_date: formatDate(sanitizedLead.first_call_date)
    };

    console.log("🔍 Final editing lead object:", finalLead);
    console.log("🔍 Checking for error objects in finalLead:");
    Object.entries(finalLead).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        console.log(`  ${key}:`, value);
      }
    });

    setEditingLead(finalLead);
    setIsEditDialogOpen(true);
  };

  const handleViewLead = async (lead) => {
    try {
      // 💡 FIX: Fetch the full lead data to guarantee contact_logs is present
      const response = await leadService.getById(lead.id || lead._id);
      setViewingLead(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch full lead details:", error);
      // Fallback if the dedicated fetch fails
      setViewingLead(lead);
      setIsViewDialogOpen(true);
    }
  };

const handleUpdateLead = async () => {
    if (!editingLead) {
      console.error("handleUpdateLead called with no editingLead data. Aborting.");
      return;
    }

    try {
      // 1. Remove metadata/read-only fields that shouldn't be sent to the API
      const { 
        id, 
        _id, 
        created_at, 
        updated_at, 
        contact_logs,
        last_contact,  // <-- ADDED THIS FIELD TO REMOVE IT
        ...updatableData 
      } = editingLead;

      // 2. Prepare the final data payload
      const leadData = {
        ...updatableData,
        project_value: parseFloat(editingLead.project_value) || 0
      };

      // 3. 💡 FIX: Manually clean up the 'notes' field.
      // The API expects a string, but the log shows it's sending an array `[]`.
      if (Array.isArray(leadData.notes)) {
        console.warn("Correcting 'notes' from array to string.");
        // Convert array to a string. If it's empty, it becomes "".
        leadData.notes = leadData.notes.join('\n'); 
      } else if (leadData.notes === null || typeof leadData.notes === 'undefined') {
        // Ensure null/undefined notes are sent as an empty string
        leadData.notes = ""; 
      }

      // 4. 💡 FIX: Convert empty date strings to null for backend validation
      const dateFields = ['first_email_date', 'first_call_date', 'appointment_date'];
      dateFields.forEach(field => {
        if (leadData[field] === "" || leadData[field] === null || leadData[field] === undefined) {
          leadData[field] = null;
        }
      });
      
      // 4. Use the mutation
      updateLeadMutation.mutate({
        id: editingLead.id || editingLead._id,
        data: leadData
      });
    } catch (error) {
      // This catch is for any other synchronous errors
      console.error("Error in updateLead function call:", error.response?.data || error);
    }
  };
  
  const [deleteClicks, setDeleteClicks] = React.useState({});

  const handleDeleteLead = async (lead) => {
    const leadId = lead.id || lead._id;
    const currentClicks = deleteClicks[leadId] || 0;
    
    if (currentClicks === 0) {
      console.warn(`⚠️ DELETE WARNING: Click delete again to confirm deletion of "${lead.name}"`);
      setDeleteClicks(prev => ({ ...prev, [leadId]: 1 }));
      setTimeout(() => {
        setDeleteClicks(prev => ({ ...prev, [leadId]: 0 }));
      }, 3000);
      return;
    }
    
    try {
      console.log(`🗑️ Deleting lead: ${lead.name}`);
      await leadService.delete(leadId);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({ description: "Lead deleted successfully!" });
    } catch (error) {
      console.error("❌ Failed to delete lead:", error);
      toast({
        variant: "destructive",
        description: "Failed to delete lead"
      });
    }
  };

  const handleConvertToCustomer = async (leadId, leadName) => {
    convertToCustomerMutation.mutate(leadId);
    toast({ description: `${leadName} successfully converted to customer!` });
  };

  const handleAddContactLog = async () => {
    if (!newContactLog.summary.trim()) return;
    
    addContactLogMutation.mutate({
      leadId: viewingLead.id || viewingLead._id,
      contactLog: newContactLog
    });
    
    // Refresh lead details after success
    const refreshLead = async () => {
      try {
        const response = await leadService.getById(viewingLead.id || viewingLead._id);
        setViewingLead(response.data);
      } catch (error) {
        console.error("Failed to refresh lead details:", error);
      }
    };
    setTimeout(refreshLead, 500); // Small delay to ensure backend has updated
  };


  // Email handling functions
  const handleSendEmail = (lead) => {
    setSelectedLead(lead);
    setEmailData({
      template_id: "",
      to_email: lead.email || "",
      to_name: lead.name || "",
      subject: "",
      body: "",
      variables: {}
    });
    setSelectedEmailTemplate(null);
    setIsSendEmailDialogOpen(true);
  };

  const handleTemplateSelect = (template) => {
    setSelectedEmailTemplate(template);
    setEmailData({
      ...emailData,
      template_id: template.id,
      subject: template.subject || "",
      body: template.body || ""
    });
  };

  // Function to preview email content with variables replaced
  const getPreviewContent = (text) => {
    if (!text) return text;
    
    let result = text;
    const lead = selectedLead || {};
    
    // Format appointment_date if present
    let formattedAppointmentDate = '';
    if (lead.appointment_date) {
      try {
        const date = new Date(lead.appointment_date);
        formattedAppointmentDate = date.toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        formattedAppointmentDate = lead.appointment_date;
      }
    }
    
    // Build a variables map from lead data
    const variables = {
      // Lead fields
      name: lead.name || '',
      last_name: lead.last_name || '',
      full_name: `${lead.name || ''} ${lead.last_name || ''}`.trim(),
      email: lead.email || '',
      phone: lead.phone || '',
      address: lead.address || '',
      city: lead.city || '',
      zip_code: lead.zip_code || '',
      spouse: lead.spouse || '',
      spouse_phone: lead.spouse_phone || '',
      spouse_email: lead.spouse_email || '',
      service_type: lead.service_type || '',
      budget: lead.budget || '',
      status: lead.status || '',
      source: lead.source || lead.referral_source || '',
      referral_source: lead.referral_source || lead.source || '',
      gate_code: lead.gate_code || '',
      appointment_date: formattedAppointmentDate,
      description: lead.description || '',
      notes: lead.notes || '',
      // Common aliases (customer_ prefix for template compatibility)
      customer_name: lead.name || '',
      customer_last_name: lead.last_name || '',
      customer_full_name: `${lead.name || ''} ${lead.last_name || ''}`.trim(),
      customer_email: lead.email || '',
      customer_phone: lead.phone || '',
      customer_address: lead.address || '',
      customer_city: lead.city || '',
      customer_zip_code: lead.zip_code || '',
      // Lead-specific aliases
      lead_name: lead.name || '',
      lead_email: lead.email || '',
      lead_phone: lead.phone || '',
    };
    
    // Replace variables
    for (const [key, value] of Object.entries(variables)) {
      const placeholder = `{${key}}`;
      if (result.includes(placeholder)) {
        result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value || `[${key}]`);
      }
    }
    
    return result;
  };

  // Check for unresolved variables
  const getUnresolvedVariables = (text) => {
    if (!text) return [];
    const variablePattern = /\{([^}]+)\}/g;
    const matches = text.match(variablePattern) || [];
    return [...new Set(matches)];
  };

  const handleSendEmailSubmit = async () => {
    const sendPayload = {
      ...emailData,
      lead_id: selectedLead.id || selectedLead._id
    };

    // Preview first to check for missing variable data
    try {
      const preview = await emailService.preview(sendPayload);
      const previewData = preview?.data || preview;
      const warnings = previewData?.warnings || [];

      if (warnings.length > 0) {
        const warningMessages = warnings.map(w => w.message || w).join('\n\n');
        toast({
          variant: "destructive",
          title: "Cannot send — missing variable data",
          description: warningMessages,
          duration: 10000
        });
        return;
      }
    } catch (err) {
      console.error("Preview check failed:", err);
    }

    sendEmailMutation.mutate(sendPayload);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-600 mt-1">Manage and track your potential customers</p>
        </div>
        {canEdit && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-600 hover:bg-brand-700 w-full sm:w-auto shrink-0">
                <Plus className="mr-2 h-4 w-4" />
                Add Lead
              </Button>
            </DialogTrigger>
          <DialogContent
            className="max-h-[100dvh] w-screen md:max-w-2xl md:max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
            onInteractOutside={(e) => {
              // Prevent dialog from closing when clicking on Google Places dropdown
              const target = e.target || e.detail?.originalEvent?.target;
              if (target?.closest?.('.pac-container')) {
                e.preventDefault();
              }
            }}
            onPointerDownOutside={(e) => {
              // Also catch pointer down on Google Places dropdown
              const target = e.target || e.detail?.originalEvent?.target;
              if (target?.closest?.('.pac-container')) {
                e.preventDefault();
              }
            }}
          >
            {/* Custom Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-4 border-b bg-gray-50 sticky top-0 z-10">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsDialogOpen(false)}
                className="text-gray-600 hover:text-gray-900 h-10 text-sm"
              >
                Cancel
              </Button>
              <span className="font-semibold text-gray-900 text-sm sm:text-base">New Lead</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAddLead}
                disabled={createLeadMutation.isPending}
                className="text-blue-600 hover:text-blue-700 font-semibold h-10 text-sm"
              >
                {createLeadMutation.isPending ? "Saving..." : "Done"}
              </Button>
            </div>

            {/* Subtitle */}
            <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-b bg-white">
              <p className="text-sm text-gray-500">Enter the details of your new potential customer</p>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6">
              <Accordion type="multiple" defaultValue={["basic", "contact"]} className="w-full">

                {/* Basic Information */}
                <AccordionItem value="basic" className="border-b">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="font-medium text-gray-900">Basic Information</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pb-2">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-xs text-gray-500 uppercase tracking-wide">First Name *</Label>
                        <Input
                          id="name"
                          value={newLead.name}
                          onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                          placeholder="John"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name" className="text-xs text-gray-500 uppercase tracking-wide">Last Name</Label>
                        <Input
                          id="last_name"
                          value={newLead.last_name}
                          onChange={(e) => setNewLead({...newLead, last_name: e.target.value})}
                          placeholder="Doe"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-xs text-gray-500 uppercase tracking-wide">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={newLead.email}
                          onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                          placeholder="john@email.com"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-xs text-gray-500 uppercase tracking-wide">Phone</Label>
                        <PhoneInput
                          id="phone"
                          value={newLead.phone}
                          onChange={(val) => setNewLead({...newLead, phone: val})}
                          placeholder="916.232.2332"
                          className="h-10"
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="company_account" className="text-xs text-gray-500 uppercase tracking-wide">Company/Account</Label>
                        <Input
                          id="company_account"
                          value={newLead.company_account}
                          onChange={(e) => setNewLead({...newLead, company_account: e.target.value})}
                          placeholder="Company name or account"
                          className="h-10"
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Additional Contact Information (Spouse) */}
                <AccordionItem value="contact" className="border-b">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <Phone className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="font-medium text-gray-900">Additional Contact Information</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pb-2">
                      <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="spouse" className="text-xs text-gray-500 uppercase tracking-wide">Spouse Name</Label>
                          <Input
                            id="spouse"
                            value={newLead.spouse}
                            onChange={(e) => setNewLead({...newLead, spouse: e.target.value})}
                            placeholder="Spouse name"
                            className="h-10"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="spouse_phone" className="text-xs text-gray-500 uppercase tracking-wide">Spouse Phone</Label>
                            <PhoneInput
                              id="spouse_phone"
                              value={newLead.spouse_phone}
                              onChange={(val) => setNewLead({...newLead, spouse_phone: val})}
                              placeholder="916.232.2332"
                              className="h-10"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="spouse_email" className="text-xs text-gray-500 uppercase tracking-wide">Spouse Email</Label>
                            <Input
                              id="spouse_email"
                              type="email"
                              value={newLead.spouse_email}
                              onChange={(e) => setNewLead({...newLead, spouse_email: e.target.value})}
                              placeholder="spouse@email.com"
                              className="h-10"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Location & Property */}
                <AccordionItem value="location" className="border-b">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="font-medium text-gray-900">Location & Property</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 sm:space-y-4 pb-2">
                      <div className="space-y-2">
                        <Label htmlFor="address" className="text-xs text-gray-500 uppercase tracking-wide">Street Address</Label>
                        <AddressAutocomplete
                          id="address"
                          onPlaceSelected={(place) => {
                            setNewLead((prevLead) => ({
                              ...prevLead,
                              address: place.address,
                              city: place.city,
                              zip_code: place.zip_code
                            }));
                          }}
                          placeholder="Start typing address..."
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="city" className="text-xs text-gray-500 uppercase tracking-wide">City</Label>
                          <Input
                            id="city"
                            value={newLead.city}
                            onChange={(e) => setNewLead({...newLead, city: e.target.value})}
                            placeholder="City"
                            className="h-10"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="zip_code" className="text-xs text-gray-500 uppercase tracking-wide">Zip Code</Label>
                          <Input
                            id="zip_code"
                            value={newLead.zip_code}
                            onChange={(e) => setNewLead({...newLead, zip_code: e.target.value})}
                            placeholder="12345"
                            className="h-10"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="gate_code" className="text-xs text-gray-500 uppercase tracking-wide">Gate Code</Label>
                          <Input
                            id="gate_code"
                            value={newLead.gate_code}
                            onChange={(e) => setNewLead({...newLead, gate_code: e.target.value})}
                            placeholder="Gate access code"
                            className="h-10"
                          />
                        </div>
                        <div className="flex items-center space-x-2 pt-6">
                          <Checkbox
                            id="hoa"
                            checked={newLead.hoa}
                            onCheckedChange={(checked) => setNewLead({...newLead, hoa: checked})}
                          />
                          <Label htmlFor="hoa" className="text-sm cursor-pointer">HOA Property</Label>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Contact Tracking */}
                <AccordionItem value="tracking" className="border-b">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-cyan-600" />
                      </div>
                      <span className="font-medium text-gray-900">Contact Tracking</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pb-2">
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="appointment_date" className="text-xs text-gray-500 uppercase tracking-wide">Appt. Date & Time</Label>
                        <DateTimePicker
                          id="appointment_date"
                          value={newLead.appointment_date}
                          onChange={(value) => setNewLead({...newLead, appointment_date: value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="first_email_date" className="text-xs text-gray-500 uppercase tracking-wide">First Email Date</Label>
                        <Input
                          id="first_email_date"
                          type="date"
                          value={newLead.first_email_date}
                          onChange={(e) => setNewLead({...newLead, first_email_date: e.target.value})}
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="first_call_date" className="text-xs text-gray-500 uppercase tracking-wide">First Call Date</Label>
                        <Input
                          id="first_call_date"
                          type="date"
                          value={newLead.first_call_date}
                          onChange={(e) => setNewLead({...newLead, first_call_date: e.target.value})}
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="source" className="text-xs text-gray-500 uppercase tracking-wide">Lead Source</Label>
                        <Select
                          value={newLead.referral_source || ""}
                          onValueChange={(value) => setNewLead({...newLead, source: value, referral_source: value})}
                        >
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select source" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            {REFERRAL_SOURCE_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-xs text-gray-500 uppercase tracking-wide">Company</Label>
                        <Select value={newLead.company || ""} onValueChange={(value) => setNewLead({...newLead, company: value})}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select company" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            <SelectItem value="Landscaping">Landscaping</SelectItem>
                            <SelectItem value="Exteriors">Exteriors</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="assigned_to" className="text-xs text-gray-500 uppercase tracking-wide">Assigned To</Label>
                        <Select
                          value={newLead.assigned_to || ""}
                          onValueChange={(value) => setNewLead({...newLead, assigned_to: value})}
                        >
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select designer (optional)" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            {(designersData?.data || []).map((designer) => (
                              <SelectItem key={designer.id || designer._id} value={designer.name}>
                                {designer.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="description" className="text-xs text-gray-500 uppercase tracking-wide">Description</Label>
                        <Textarea
                          id="description"
                          value={newLead.description}
                          onChange={(e) => setNewLead({...newLead, description: e.target.value})}
                          placeholder="Brief description of the lead or project..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Optional Info (formerly Project Details) */}
                <AccordionItem value="project" className="border-b">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                        <Building className="w-4 h-4 text-orange-600" />
                      </div>
                      <span className="font-medium text-gray-900">Optional Info</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pb-2">
                      <div className="space-y-2">
                        <Label htmlFor="serviceType" className="text-xs text-gray-500 uppercase tracking-wide">Service Type</Label>
                        <Select
                          value={newLead.service_type || ""}
                          onValueChange={(value) => setNewLead({...newLead, service_type: value})}
                        >
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            <SelectItem value="garden_design">Design Only</SelectItem>
                            <SelectItem value="lawn_installation">Design and Construction</SelectItem>
                            <SelectItem value="complete_landscape">Complete Landscape</SelectItem>
                            <SelectItem value="maintenance">Hardscaping only</SelectItem>
                            <SelectItem value="concrete_only">Concrete Only</SelectItem>
                            <SelectItem value="frontyard_only">Front Yard Only</SelectItem>
                            <SelectItem value="backyard_only">Back Yard Only</SelectItem>
                            <SelectItem value="front_and_backyard">Front and Backyard</SelectItem>
                            <SelectItem value="irrigation">Irrigation Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="budget" className="text-xs text-gray-500 uppercase tracking-wide">Budget</Label>
                        <Input
                          id="budget"
                          value={newLead.budget}
                          onChange={(e) => setNewLead({...newLead, budget: e.target.value})}
                          placeholder="$10,000 - $20,000"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="projectValue" className="text-xs text-gray-500 uppercase tracking-wide">Estimated Value</Label>
                        <Input
                          id="projectValue"
                          type="number"
                          value={newLead.project_value}
                          onChange={(e) => setNewLead({...newLead, project_value: e.target.value})}
                          placeholder="10000"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="priority" className="text-xs text-gray-500 uppercase tracking-wide">Priority</Label>
                        <Select value={newLead.priority || "medium"} onValueChange={(value) => setNewLead({...newLead, priority: value})}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="status" className="text-xs text-gray-500 uppercase tracking-wide">Status</Label>
                        <Select value={newLead.status || "New"} onValueChange={(value) => setNewLead({...newLead, status: value})}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            {STATUS_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="notes" className="text-xs text-gray-500 uppercase tracking-wide">Internal Notes</Label>
                        <Textarea
                          id="notes"
                          value={newLead.notes}
                          onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                          placeholder="Any additional internal notes about this lead..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            
            {/* Footer Button - Always visible */}
            <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t bg-gray-50 sticky bottom-0 z-10">
              <Button
                onClick={handleAddLead}
                disabled={createLeadMutation.isPending}
                className="w-full bg-blue-600 hover:bg-blue-700 h-11"
              >
                {createLeadMutation.isPending ? "Creating..." : "Add Lead"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search leads by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page when searching
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select 
                value={filterStatus} 
                onValueChange={(value) => {
                  setFilterStatus(value);
                  setCurrentPage(1); // Reset to first page when filtering
                }}
              >
                <SelectTrigger className="w-40">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Leads ({leadsData?.pagination?.total || leads.length})</CardTitle>
          <CardDescription>
            Track and manage your potential customers
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingCard>Loading leads...</LoadingCard>
          ) : (
            <>
            {/* Mobile card view */}
            <div className="md:hidden space-y-3">
              {leads.map((lead) => (
                <div key={lead._id || lead.id} className="bg-white border rounded-lg p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 truncate">
                        {lead.last_name ? `${lead.last_name}, ${lead.name}` : lead.name || 'No Name'}
                      </p>
                      {lead.phone && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <Phone className="h-3 w-3 flex-shrink-0" />{formatPhone(lead.phone)}
                        </p>
                      )}
                      {lead.email && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Mail className="h-3 w-3 flex-shrink-0" /><span className="truncate">{lead.email}</span>
                        </p>
                      )}
                    </div>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ml-2 shrink-0 ${getStatusColor(lead.status)} text-white`}>
                      {getStatusLabel(lead.status)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {(lead.source || lead.referral_source) && <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">{lead.source || lead.referral_source}</span>}
                      {lead.company && (
                        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white font-bold text-[10px] ${lead.company === "Landscaping" ? "bg-blue-500" : "bg-red-500"}`}>
                          {lead.company === "Landscaping" ? "L" : "E"}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleViewLead(lead)} className="h-8 w-8 p-0">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleSendEmail(lead)} disabled={!lead.email} className="h-8 w-8 p-0">
                        <Mail className="h-4 w-4" />
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => handleEditLead(lead)} className="h-8 w-8 p-0">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block">
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Last Contact</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((lead) => (
                <TableRow key={lead.id || lead._id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {lead.last_name ? `${lead.last_name}, ${lead.name}` : lead.name}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <Mail className="mr-1 h-3 w-3" />
                        {lead.email}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <Phone className="mr-1 h-3 w-3" />
                        {formatPhone(lead.phone)}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {lead.service_type ? lead.service_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center">
                      <MapPin className="mr-1 h-3 w-3" />
                      {[lead.address, lead.city, lead.zip_code].filter(Boolean).join(', ') || 'No address'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {lead.company ? (
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white font-bold text-sm ${lead.company === "Landscaping" ? "bg-blue-500" : "bg-red-500"}`}>
                        {lead.company === "Landscaping" ? "L" : "E"}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select 
                      value={lead.status} 
                      onValueChange={(value) => updateLeadStatus(lead.id || lead._id, value)}
                    >
                      <SelectTrigger className="w-32">
                        <Badge className={`${getStatusColor(lead.status)} text-white text-xs`}>
                          {getStatusLabel(lead.status)}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{lead.source || lead.referral_source || 'N/A'}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {lead.last_contact ? new Date(lead.last_contact).toLocaleDateString() : 'Never'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleSendEmail(lead)}
                        disabled={!lead.email}
                      >
                        <Mail className="mr-1 h-3 w-3" />
                        Email
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleViewLead(lead)}
                      >
                        <Eye className="mr-1 h-3 w-3" />
                        View
                      </Button>
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditLead(lead)}
                        >
                          <Edit className="mr-1 h-3 w-3" />
                          Edit
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteLead(lead)}
                          className={`${
                            (deleteClicks[lead.id || lead._id] || 0) > 0
                              ? "text-white bg-red-600 hover:bg-red-700"
                              : "text-red-600 hover:text-red-700 hover:bg-red-50"
                          }`}
                          title={
                            (deleteClicks[lead.id || lead._id] || 0) > 0
                              ? "Click again to confirm deletion"
                              : "Delete lead"
                          }
                        >
                          <Trash2 className="mr-1 h-3 w-3" />
                          {(deleteClicks[lead.id || lead._id] || 0) > 0 ? "Confirm?" : "Delete"}
                        </Button>
                      )}
                      {lead.status !== "Converted" && (
                        <Button 
                          size="sm" 
                          onClick={() => handleConvertToCustomer(lead.id, lead.name)}
                          disabled={convertToCustomerMutation.isPending}
                          className="bg-brand-600 hover:bg-brand-700"
                        >
                          <UserPlus className="mr-1 h-3 w-3" />
                          Create Customer
                        </Button>
                      )}
                      {lead.status === "Converted" && (
                        <Badge className="bg-brand-light text-brand-700">
                          Converted
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          </>
          )}

          {/* Pagination Controls */}
          {leadsData && (
            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * leadsPerPage) + 1} to{" "}
                {Math.min(currentPage * leadsPerPage, leadsData.pagination?.total || 0)} of{" "}
                {leadsData.pagination?.total || 0} leads
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={!leadsData.pagination?.has_prev}
                >
                  Previous
                </Button>
                <div className="text-sm">
                  Page {currentPage} of {leadsData.pagination?.total_pages || 1}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!leadsData.pagination?.has_next}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Lead Dialog - Matching New Lead style */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent
          className="max-h-[100dvh] w-screen md:max-w-2xl md:max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            // Prevent dialog from closing when clicking on Google Places dropdown
            const target = e.target || e.detail?.originalEvent?.target;
            if (target?.closest?.('.pac-container')) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={(e) => {
            // Also catch pointer down on Google Places dropdown
            const target = e.target || e.detail?.originalEvent?.target;
            if (target?.closest?.('.pac-container')) {
              e.preventDefault();
            }
          }}
        >
          {/* Custom Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-4 border-b bg-gray-50 sticky top-0 z-10">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditDialogOpen(false)}
              className="text-gray-600 hover:text-gray-900 h-10 text-sm"
            >
              Cancel
            </Button>
            <span className="font-semibold text-gray-900 text-sm sm:text-base">Edit Lead</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleUpdateLead}
              disabled={updateLeadMutation.isPending || !editingLead?.name}
              className="text-blue-600 hover:text-blue-700 font-semibold h-10 text-sm"
            >
              {updateLeadMutation.isPending ? "Saving..." : "Done"}
            </Button>
          </div>

          {/* Subtitle */}
          <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-b bg-white">
            <p className="text-sm text-gray-500">Update the lead information and status</p>
          </div>
          
          {editingLead && (
            <>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6">
              <Accordion type="multiple" defaultValue={["basic", "contact"]} className="w-full">

                {/* Basic Information */}
                <AccordionItem value="basic" className="border-b">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="font-medium text-gray-900">Basic Information</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pb-2">
                      <div className="space-y-2">
                        <Label htmlFor="editName" className="text-xs text-gray-500 uppercase tracking-wide">First Name *</Label>
                        <Input
                          id="editName"
                          value={editingLead.name || ""}
                          onChange={(e) => setEditingLead({...editingLead, name: e.target.value})}
                          placeholder="John"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editLastName" className="text-xs text-gray-500 uppercase tracking-wide">Last Name</Label>
                        <Input
                          id="editLastName"
                          value={editingLead.last_name || ""}
                          onChange={(e) => setEditingLead({...editingLead, last_name: e.target.value})}
                          placeholder="Doe"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editEmail" className="text-xs text-gray-500 uppercase tracking-wide">Email</Label>
                        <Input
                          id="editEmail"
                          type="email"
                          value={editingLead.email || ""}
                          onChange={(e) => setEditingLead({...editingLead, email: e.target.value})}
                          placeholder="john@email.com"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editPhone" className="text-xs text-gray-500 uppercase tracking-wide">Phone</Label>
                        <PhoneInput
                          id="editPhone"
                          value={editingLead.phone || ""}
                          onChange={(val) => setEditingLead({...editingLead, phone: val})}
                          placeholder="916.232.2332"
                          className="h-10"
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="editCompanyAccount" className="text-xs text-gray-500 uppercase tracking-wide">Company/Account</Label>
                        <Input
                          id="editCompanyAccount"
                          value={editingLead.company_account || ""}
                          onChange={(e) => setEditingLead({...editingLead, company_account: e.target.value})}
                          placeholder="Company name or account"
                          className="h-10"
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Additional Contact Information (Spouse) */}
                <AccordionItem value="contact" className="border-b">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <Phone className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="font-medium text-gray-900">Additional Contact Information</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pb-2">
                      <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="editSpouse" className="text-xs text-gray-500 uppercase tracking-wide">Spouse Name</Label>
                          <Input
                            id="editSpouse"
                            value={editingLead.spouse || ""}
                            onChange={(e) => setEditingLead({...editingLead, spouse: e.target.value})}
                            placeholder="Spouse name"
                            className="h-10"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="editSpousePhone" className="text-xs text-gray-500 uppercase tracking-wide">Spouse Phone</Label>
                            <PhoneInput
                              id="editSpousePhone"
                              value={editingLead.spouse_phone || ""}
                              onChange={(val) => setEditingLead({...editingLead, spouse_phone: val})}
                              placeholder="916.232.2332"
                              className="h-10"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="editSpouseEmail" className="text-xs text-gray-500 uppercase tracking-wide">Spouse Email</Label>
                            <Input
                              id="editSpouseEmail"
                              type="email"
                              value={editingLead.spouse_email || ""}
                              onChange={(e) => setEditingLead({...editingLead, spouse_email: e.target.value})}
                              placeholder="spouse@email.com"
                              className="h-10"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Location & Property */}
                <AccordionItem value="location" className="border-b">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="font-medium text-gray-900">Location & Property</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pb-2">
                      <div className="space-y-2">
                        <Label htmlFor="editAddress" className="text-xs text-gray-500 uppercase tracking-wide">Street Address</Label>
                        <AddressAutocomplete
                          id="editAddress"
                          defaultValue={editingLead.address || ""}
                          onPlaceSelected={(place) => {
                            setEditingLead((prev) => ({
                              ...prev,
                              address: place.address,
                              city: place.city,
                              zip_code: place.zip_code
                            }));
                          }}
                          onChange={(e) => setEditingLead({...editingLead, address: e.target.value})}
                          placeholder="Start typing address..."
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="editCity" className="text-xs text-gray-500 uppercase tracking-wide">City</Label>
                          <Input
                            id="editCity"
                            value={editingLead.city || ""}
                            onChange={(e) => setEditingLead({...editingLead, city: e.target.value})}
                            placeholder="City"
                            className="h-10"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="editZipCode" className="text-xs text-gray-500 uppercase tracking-wide">Zip Code</Label>
                          <Input
                            id="editZipCode"
                            value={editingLead.zip_code || ""}
                            onChange={(e) => setEditingLead({...editingLead, zip_code: e.target.value})}
                            placeholder="12345"
                            className="h-10"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="editGateCode" className="text-xs text-gray-500 uppercase tracking-wide">Gate Code</Label>
                          <Input
                            id="editGateCode"
                            value={editingLead.gate_code || ""}
                            onChange={(e) => setEditingLead({...editingLead, gate_code: e.target.value})}
                            placeholder="Gate access code"
                            className="h-10"
                          />
                        </div>
                        <div className="flex items-center space-x-2 pt-6">
                          <Checkbox
                            id="editHoa"
                            checked={editingLead.hoa || false}
                            onCheckedChange={(checked) => setEditingLead({...editingLead, hoa: checked})}
                          />
                          <Label htmlFor="editHoa" className="cursor-pointer">HOA Property</Label>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Contact Tracking */}
                <AccordionItem value="tracking" className="border-b">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-cyan-600" />
                      </div>
                      <span className="font-medium text-gray-900">Contact Tracking</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pb-2">
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="editAppointmentDate" className="text-xs text-gray-500 uppercase tracking-wide">Appt. Date & Time</Label>
                        <DateTimePicker
                          id="editAppointmentDate"
                          value={editingLead.appointment_date || ""}
                          onChange={(value) => setEditingLead({...editingLead, appointment_date: value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editFirstEmailDate" className="text-xs text-gray-500 uppercase tracking-wide">First Email Date</Label>
                        <Input
                          id="editFirstEmailDate"
                          type="date"
                          value={editingLead.first_email_date || ""}
                          onChange={(e) => setEditingLead({...editingLead, first_email_date: e.target.value})}
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editFirstCallDate" className="text-xs text-gray-500 uppercase tracking-wide">First Call Date</Label>
                        <Input
                          id="editFirstCallDate"
                          type="date"
                          value={editingLead.first_call_date || ""}
                          onChange={(e) => setEditingLead({...editingLead, first_call_date: e.target.value})}
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editSource" className="text-xs text-gray-500 uppercase tracking-wide">Lead Source</Label>
                        <Select value={editingLead.referral_source || editingLead.source || ""} onValueChange={(value) => setEditingLead({...editingLead, source: value, referral_source: value})}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select source" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            {REFERRAL_SOURCE_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editCompany" className="text-xs text-gray-500 uppercase tracking-wide">Company</Label>
                        <Select value={editingLead.company || ""} onValueChange={(value) => setEditingLead({...editingLead, company: value})}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select company" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            <SelectItem value="Landscaping">Landscaping</SelectItem>
                            <SelectItem value="Exteriors">Exteriors</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="editAssignedTo" className="text-xs text-gray-500 uppercase tracking-wide">Assigned To</Label>
                        <Select value={editingLead.assigned_to || ""} onValueChange={(value) => setEditingLead({...editingLead, assigned_to: value})}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select designer (optional)" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            {(designersData?.data || []).map((designer) => (
                              <SelectItem key={designer.id || designer._id} value={designer.name}>
                                {designer.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="editDescription" className="text-xs text-gray-500 uppercase tracking-wide">Description</Label>
                        <Textarea
                          id="editDescription"
                          value={editingLead.description || ""}
                          onChange={(e) => setEditingLead({...editingLead, description: e.target.value})}
                          placeholder="Brief description of the lead or project..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Optional Info */}
                <AccordionItem value="project" className="border-b">
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                        <Building className="w-4 h-4 text-orange-600" />
                      </div>
                      <span className="font-medium text-gray-900">Optional Info</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pb-2">
                      <div className="space-y-2">
                        <Label htmlFor="editServiceType" className="text-xs text-gray-500 uppercase tracking-wide">Service Type</Label>
                        <Select value={editingLead.service_type || ""} onValueChange={(value) => setEditingLead({...editingLead, service_type: value})}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            <SelectItem value="garden_design">Design Only</SelectItem>
                            <SelectItem value="lawn_installation">Design and Construction</SelectItem>
                            <SelectItem value="complete_landscape">Complete Landscape</SelectItem>
                            <SelectItem value="maintenance">Hardscaping only</SelectItem>
                            <SelectItem value="concrete_only">Concrete Only</SelectItem>
                            <SelectItem value="frontyard_only">Front Yard Only</SelectItem>
                            <SelectItem value="backyard_only">Back Yard Only</SelectItem>
                            <SelectItem value="front_and_backyard">Front and Backyard</SelectItem>
                            <SelectItem value="irrigation">Irrigation Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editBudget" className="text-xs text-gray-500 uppercase tracking-wide">Budget</Label>
                        <Input
                          id="editBudget"
                          value={editingLead.budget || ""}
                          onChange={(e) => setEditingLead({...editingLead, budget: e.target.value})}
                          placeholder="$10,000 - $20,000"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editProjectValue" className="text-xs text-gray-500 uppercase tracking-wide">Estimated Value</Label>
                        <Input
                          id="editProjectValue"
                          type="number"
                          value={editingLead.project_value || ""}
                          onChange={(e) => setEditingLead({...editingLead, project_value: e.target.value})}
                          placeholder="10000"
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editPriority" className="text-xs text-gray-500 uppercase tracking-wide">Priority</Label>
                        <Select value={editingLead.priority || "medium"} onValueChange={(value) => setEditingLead({...editingLead, priority: value})}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="editStatus" className="text-xs text-gray-500 uppercase tracking-wide">Status</Label>
                        <Select value={editingLead.status || "New"} onValueChange={(value) => setEditingLead({...editingLead, status: value})}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent position="popper" sideOffset={5}>
                            {STATUS_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="editNotes" className="text-xs text-gray-500 uppercase tracking-wide">Internal Notes</Label>
                        <Textarea
                          id="editNotes"
                          value={editingLead.notes || ""}
                          onChange={(e) => setEditingLead({...editingLead, notes: e.target.value})}
                          placeholder="Any additional internal notes about this lead..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            
            {/* Footer Button - Always visible */}
            <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t bg-gray-50 sticky bottom-0 z-10">
              <Button
                onClick={handleUpdateLead}
                disabled={updateLeadMutation.isPending || !editingLead?.name}
                className="w-full bg-blue-600 hover:bg-blue-700 h-11"
              >
                {updateLeadMutation.isPending ? "Updating..." : "Update Lead"}
              </Button>
            </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={isSendEmailDialogOpen} onOpenChange={setIsSendEmailDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Send Email to Lead</DialogTitle>
            <DialogDescription>
              Send an email to {selectedLead?.name} using a template.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0">
            <div className="space-y-2">
              <Label htmlFor="emailTemplate">Email Template</Label>
              <Select 
                value={emailData.template_id} 
                onValueChange={(templateId) => {
                  const template = emailTemplatesData?.data?.templates?.find(t => t.id === templateId);
                  if (template) {
                    handleTemplateSelect(template);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select email template" />
                </SelectTrigger>
                <SelectContent>
                  {emailTemplatesData?.data?.templates?.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="emailSubject">Subject</Label>
              <Input
                id="emailSubject"
                value={emailData.subject}
                onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                placeholder="Enter email subject"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="emailBody">Message</Label>
              <Textarea
                id="emailBody"
                value={emailData.body}
                onChange={(e) => setEmailData({...emailData, body: e.target.value})}
                placeholder="Enter email body"
                rows={6}
              />
            </div>

            {/* Email Preview for all HTML emails */}
            {emailData.body && (
              <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
                <Label className="text-blue-800 font-medium flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Email Preview
                </Label>
                <div className="text-sm bg-white p-3 rounded border max-h-48 overflow-y-auto overflow-x-hidden">
                  <div className="font-medium text-gray-700 mb-2 truncate">Subject: {getPreviewContent(emailData.subject)}</div>
                  <div 
                    className="text-gray-600 prose prose-sm max-w-full break-words [&_*]:max-w-full [&_img]:max-w-full [&_table]:max-w-full [&_table]:table-fixed"
                    dangerouslySetInnerHTML={{ __html: getPreviewContent(emailData.body) }}
                  />
                </div>
                {getUnresolvedVariables(getPreviewContent(emailData.subject + emailData.body)).length > 0 && (
                  <div className="text-amber-700 text-xs flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Some variables may not have data: {getUnresolvedVariables(getPreviewContent(emailData.subject + emailData.body)).join(', ')}
                  </div>
                )}
              </div>
            )}

            {selectedEmailTemplate && selectedEmailTemplate.variables?.length > 0 && (
              <div className="space-y-2">
                <Label>Template Variables:</Label>
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  {selectedEmailTemplate.variables?.map((variable, index) => (
                    <span key={variable} className="mr-2">
                      {`{${variable}}`}
                      {index < selectedEmailTemplate.variables.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSendEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmailSubmit}
              disabled={sendEmailMutation.isPending || !emailData.subject || !emailData.body || !emailData.to_email || !emailData.to_name}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {sendEmailMutation.isPending ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Lead Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={(open) => {
          if (!open) { setIsViewDialogOpen(false); setViewingLead(null); }
      }}>
          <DialogContent
            className="max-h-[100dvh] w-screen md:max-w-2xl lg:max-w-[900px] md:max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 bg-white border-b p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                {viewingLead ? (
                  <>
                  <div className="flex items-center space-x-3 min-w-0">
                    <Avatar className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0">
                      <AvatarImage src={`https://avatar.vercel.sh/${viewingLead.email || viewingLead.name}`} alt={viewingLead.name}/>
                      <AvatarFallback>{viewingLead.name ? viewingLead.name.charAt(0).toUpperCase() : '?'}{viewingLead.last_name ? viewingLead.last_name.charAt(0).toUpperCase() : ''}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="text-lg sm:text-xl font-bold truncate">{viewingLead.name} {viewingLead.last_name || ''}</div>
                      <div className="text-sm text-gray-500 font-normal truncate">{viewingLead.email}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 flex-shrink-0">
                    <Badge className={`${getStatusColor(viewingLead.status)} text-white px-2 py-0.5 text-xs`}>{viewingLead.status || 'Unknown'}</Badge>
                    <Badge variant="outline" className="capitalize text-xs">{viewingLead.priority || 'medium'}</Badge>
                    <Button size="sm" variant="outline" onClick={() => { handleEditLead(viewingLead); setIsViewDialogOpen(false); }} className="h-9 text-sm"><Edit className="h-4 w-4 mr-1" />Edit</Button>
                    <Button size="sm" onClick={() => setIsContactLogDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700 h-9 text-sm"><Phone className="h-4 w-4 mr-1" />Log Contact</Button>
                  </div>
                  </>
                ) : (
                  <span>Loading Lead...</span>
                )}
              </div>
            </div>
            {!viewingLead ? (
                <div className="flex justify-center items-center py-10">
                    <LoadingSpinner />
                </div>
            ) : (
             <Tabs defaultValue="overview" className="flex flex-col flex-1 min-h-0">
              {/* Tabs Navigation */}
              <div className="flex-shrink-0 bg-white border-b px-4">
                <div className="overflow-x-auto">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="overview" className="text-xs sm:text-sm px-2.5 sm:px-3">Overview</TabsTrigger>
                    <TabsTrigger value="activity" className="text-xs sm:text-sm px-2.5 sm:px-3">Activity</TabsTrigger>
                  </TabsList>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1 p-4 sm:p-6">
                {/* Overview Tab Content */}
                <TabsContent value="overview" className="space-y-4 mt-0">
                     {/* Company Badge */}
                     {viewingLead.company && (
                       <div className="flex items-center gap-2 mb-2">
                         <span className="text-xs text-muted-foreground uppercase tracking-wide">Company:</span>
                         <Badge className={viewingLead.company === "Landscaping" ? "bg-blue-500 hover:bg-blue-600" : "bg-red-500 hover:bg-red-600"}>
                           {viewingLead.company}
                         </Badge>
                       </div>
                     )}

                     {/* Row 1: Contact & Lead Summary */}
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div className="p-4 rounded-lg border">
                           <h4 className="text-sm font-semibold flex items-center mb-3"><User className="h-4 w-4 mr-2" />Contact Information</h4>
                           <div className="space-y-2.5">
                              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Name</p><p className="text-sm font-medium">{viewingLead.name || 'N/A'}{viewingLead.last_name ? ` ${viewingLead.last_name}` : ''}</p></div>
                              {viewingLead.company_account && <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Company</p><p className="text-sm font-medium">{viewingLead.company_account}</p></div>}
                              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Email</p><p className="text-sm font-medium">{viewingLead.email || 'N/A'}</p></div>
                              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Phone</p><p className="text-sm font-medium">{viewingLead.phone ? formatPhone(viewingLead.phone) : 'N/A'}</p></div>
                              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Address</p><p className="text-sm font-medium">{viewingLead.address || 'N/A'}{viewingLead.city || viewingLead.zip_code ? `, ${[viewingLead.city, viewingLead.zip_code].filter(Boolean).join(' ')}` : ''}</p></div>
                              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Source</p><p className="text-sm font-medium">{viewingLead.source || viewingLead.referral_source || 'N/A'}</p></div>
                           </div>
                        </div>
                        <div className="p-4 rounded-lg border">
                           <h4 className="text-sm font-semibold flex items-center mb-3"><DollarSign className="h-4 w-4 mr-2" />Lead Summary</h4>
                           <div className="space-y-2.5">
                              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground uppercase tracking-wide">Status</span><Badge className={`${getStatusColor(viewingLead.status)} text-white px-2 py-0.5 text-xs`}>{viewingLead.status || 'Unknown'}</Badge></div>
                              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground uppercase tracking-wide">Priority</span><Badge variant="outline" className="capitalize">{viewingLead.priority || 'medium'}</Badge></div>
                              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground uppercase tracking-wide">Created</span><span className="text-sm font-medium">{viewingLead.created_at ? new Date(viewingLead.created_at).toLocaleDateString() : 'N/A'}</span></div>
                              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground uppercase tracking-wide">Service Type</span><span className="text-sm font-medium">{viewingLead.service_type ? viewingLead.service_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}</span></div>
                              <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground uppercase tracking-wide">Est. Value</span><span className="text-sm font-semibold text-green-600">${(viewingLead.project_value || 0).toLocaleString()}</span></div>
                           </div>
                        </div>
                     </div>
                      {/* Row 2: Additional Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                          {viewingLead.description && (
                              <Card>
                                  <CardHeader><CardTitle className="text-base flex items-center"><FileText className="h-4 w-4 mr-2" />Description</CardTitle></CardHeader>
                                  <CardContent className="space-y-2 text-sm">
                                      <p className="text-gray-600">{viewingLead.description}</p>
                                  </CardContent>
                              </Card>
                          )}
                           {(viewingLead.spouse || viewingLead.spouse_phone || viewingLead.spouse_email) && (
                              <Card>
                                 <CardHeader><CardTitle className="text-base flex items-center"><Users className="h-4 w-4 mr-2" />Spouse Information</CardTitle></CardHeader>
                                 <CardContent className="space-y-2 text-sm">
                                    {viewingLead.spouse && <div className="flex justify-between"><span>Name:</span><span>{viewingLead.spouse}</span></div>}
                                    {viewingLead.spouse_phone && <div className="flex items-center"><Phone className="mr-2 h-4 w-4 text-gray-400" />{formatPhone(viewingLead.spouse_phone)}</div>}
                                    {viewingLead.spouse_email && <div className="flex items-center"><Mail className="mr-2 h-4 w-4 text-gray-400" />{viewingLead.spouse_email}</div>}
                                 </CardContent>
                              </Card>
                           )}
                           {(viewingLead.hoa !== undefined || viewingLead.gate_code) && (
                              <Card>
                                 <CardHeader><CardTitle className="text-base flex items-center"><MapPin className="h-4 w-4 mr-2" />Property Information</CardTitle></CardHeader>
                                 <CardContent className="space-y-2 text-sm">
                                    {viewingLead.hoa !== undefined && <div className="flex justify-between"><span>HOA Property:</span><Badge className={viewingLead.hoa ? "bg-green-500" : "bg-gray-500"}>{viewingLead.hoa ? "Yes" : "No"}</Badge></div>}
                                    {viewingLead.gate_code && <div className="flex justify-between"><span>Gate Code:</span><span className="font-mono">{viewingLead.gate_code}</span></div>}
                                 </CardContent>
                              </Card>
                           )}
                           {(viewingLead.first_email_date || viewingLead.first_call_date || viewingLead.appointment_date) && (
                              <Card>
                                 <CardHeader><CardTitle className="text-base flex items-center"><Calendar className="h-4 w-4 mr-2" />Important Dates</CardTitle></CardHeader>
                                 <CardContent className="space-y-2 text-sm">
                                    {viewingLead.first_email_date && <div className="flex justify-between"><span>First Email:</span><span>{new Date(viewingLead.first_email_date).toLocaleDateString()}</span></div>}
                                    {viewingLead.first_call_date && <div className="flex justify-between"><span>First Call:</span><span>{new Date(viewingLead.first_call_date).toLocaleDateString()}</span></div>}
                                    {viewingLead.appointment_date && <div className="flex justify-between"><span>Appointment:</span><span>{formatActivityTimePST(viewingLead.appointment_date)}</span></div>}
                                 </CardContent>
                              </Card>
                           )}
                           {viewingLead.budget && (
                              <Card>
                                 <CardHeader><CardTitle className="text-base flex items-center"><DollarSign className="h-4 w-4 mr-2" />Budget</CardTitle></CardHeader>
                                 <CardContent className="text-sm"><div className="flex justify-between"><span>Budget:</span><span className="font-semibold text-green-600">{viewingLead.budget}</span></div></CardContent>
                              </Card>
                           )}
                           {viewingLead.assigned_to && (
                              <Card>
                                 <CardHeader><CardTitle className="text-base flex items-center"><User className="h-4 w-4 mr-2" />Assignment</CardTitle></CardHeader>
                                 <CardContent className="text-sm"><div className="flex justify-between"><span>Assigned To:</span><span className="font-medium">{viewingLead.assigned_to}</span></div></CardContent>
                              </Card>
                           )}
                      </div>
                       {/* Row 3: Notes & Quick Actions */}
                       <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                           {viewingLead.notes && (
                             <Card className="sm:col-span-2">
                               <CardHeader><CardTitle className="text-base flex items-center"><FileText className="h-4 w-4 mr-2" />Internal Notes</CardTitle></CardHeader>
                               <CardContent>
                                 <p className="text-sm text-gray-700">{viewingLead.notes}</p>
                               </CardContent>
                             </Card>
                           )}
                           <Card className={viewingLead.notes ? "" : "md:col-span-3"}>
                               <CardHeader><CardTitle className="text-base">Quick Actions</CardTitle></CardHeader>
                               <CardContent className="space-y-3">
                                   {viewingLead.status !== "Converted" && (
                                     <Button className="w-full bg-brand-600 hover:bg-brand-700 h-10" onClick={() => { handleConvertToCustomer(viewingLead.id || viewingLead._id, viewingLead.name); setIsViewDialogOpen(false); }}><UserPlus className="h-4 w-4 mr-2" />Convert to Customer</Button>
                                   )}
                                   <Button className="w-full h-10" variant="outline" onClick={() => setIsContactLogDialogOpen(true)}><Phone className="h-4 w-4 mr-2" />Log Contact</Button>
                                   <Button className="w-full h-10" variant="outline" onClick={() => handleSendEmail(viewingLead)} disabled={!viewingLead.email}><Mail className="h-4 w-4 mr-2" />Send Email</Button>
                               </CardContent>
                           </Card>
                       </div>
                  </TabsContent>

                  {/* Activity Tab Content */}
                  <TabsContent value="activity" className="space-y-4 mt-0">
                      <Card>
                        <CardHeader><CardTitle className="text-base">Recent Activity & Notes</CardTitle></CardHeader>
                        <CardContent>
                           <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                              {/* Contact Logs */}
                              {viewingLead.contact_logs && Array.isArray(viewingLead.contact_logs) && viewingLead.contact_logs.length > 0 ? (
                                viewingLead.contact_logs.slice().reverse().map((log, index) => {
                                  const actualIndex = viewingLead.contact_logs.length - 1 - index;
                                  return (
                                    <div key={`contact-${index}`} className="border-l-4 border-green-200 pl-4 py-2 bg-green-50/50 rounded-r-md">
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center space-x-2">
                                          {log.contact_type === 'email' ? <Mail className="h-4 w-4 text-green-500"/> : <Phone className="h-4 w-4 text-green-500"/>}
                                          <span className="text-sm font-medium text-green-700">{log.contact_type?.toUpperCase()} ({log.contact_method})</span>
                                          {log.created_at && (<span className="text-xs text-gray-500">{formatActivityTimePST(log.created_at)}</span>)}
                                        </div>
                                        {canEdit && (
                                          <div className="flex items-center space-x-1">
                                            <Button 
                                              variant="ghost" 
                                              size="sm" 
                                              onClick={() => {
                                                setEditingContactLog({
                                                  contact_type: log.contact_type,
                                                  contact_method: log.contact_method,
                                                  summary: log.summary
                                                });
                                                setEditingContactLogIndex(actualIndex);
                                              }} 
                                              className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                                            >
                                              <Edit className="h-3 w-3" />
                                            </Button>
                                            <Button 
                                              variant="ghost" 
                                              size="sm" 
                                              onClick={() => setDeletingContactLogIndex(actualIndex)} 
                                              disabled={deleteContactLogMutation.isPending} 
                                              className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-700">{log.summary}</p>
                                      {log.contact_type === 'email' && log.details && (
                                          <div className="mt-2 text-xs text-gray-500 border-t pt-1">
                                              <p><strong>Subject:</strong> {log.details.subject}</p>
                                          </div>
                                      )}
                                    </div>
                                  );
                                })
                              ) : null}
                              {/* No Activity Message */}
                              {(!viewingLead.contact_logs || viewingLead.contact_logs.length === 0) && (
                                <div className="text-center py-8"><MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No activity recorded yet</p><p className="text-sm text-gray-400">Add contacts or send emails to track activity</p></div>
                              )}
                           </div>
                           {/* Action buttons moved outside scroll area */}
                           <div className="flex gap-3 pt-4 border-t">
                              <Button onClick={() => setIsContactLogDialogOpen(true)} variant="outline" className="flex-1 h-10"><Phone className="h-4 w-4 mr-2" />Log Contact</Button>
                              <Button onClick={() => handleSendEmail(viewingLead)} disabled={!viewingLead.email} className="flex-1 h-10 bg-brand-600 hover:bg-brand-700"><Mail className="h-4 w-4 mr-2" />Send Email</Button>
                            </div>
                        </CardContent>
                      </Card>
                  </TabsContent>
              </div>
             </Tabs>
            )}
          </DialogContent>
      </Dialog>

      {/* Add Contact Log Dialog */}
      <Dialog open={isContactLogDialogOpen} onOpenChange={setIsContactLogDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Contact Log</DialogTitle>
            <DialogDescription>
              Log a communication with the lead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactType">Contact Type *</Label>
                <Select 
                  value={newContactLog.contact_type} 
                  onValueChange={(value) => setNewContactLog({...newContactLog, contact_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="in_person">In Person</SelectItem>
                    <SelectItem value="text">Text Message</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactMethod">Contact Method *</Label>
                <Select 
                  value={newContactLog.contact_method} 
                  onValueChange={(value) => setNewContactLog({...newContactLog, contact_method: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outbound">Outbound</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactSummary">Summary *</Label>
              <Textarea
                id="contactSummary"
                value={newContactLog.summary}
                onChange={(e) => setNewContactLog({...newContactLog, summary: e.target.value})}
                placeholder="Brief summary of the contact..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsContactLogDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddContactLog}
              disabled={addContactLogMutation.isPending || !newContactLog.summary.trim()}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {addContactLogMutation.isPending ? "Adding..." : "Add Contact Log"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Log Dialog */}
      <Dialog open={editingContactLog !== null} onOpenChange={(open) => { if (!open) { setEditingContactLog(null); setEditingContactLogIndex(null); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Contact Log</DialogTitle>
            <DialogDescription>
              Update the contact log entry.
            </DialogDescription>
          </DialogHeader>
          {editingContactLog && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editContactType">Contact Type *</Label>
                  <Select 
                    value={editingContactLog.contact_type} 
                    onValueChange={(value) => setEditingContactLog({...editingContactLog, contact_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={5}>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="in_person">In Person</SelectItem>
                      <SelectItem value="text">Text Message</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editContactMethod">Contact Method *</Label>
                  <Select 
                    value={editingContactLog.contact_method} 
                    onValueChange={(value) => setEditingContactLog({...editingContactLog, contact_method: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={5}>
                      <SelectItem value="outbound">Outbound</SelectItem>
                      <SelectItem value="inbound">Inbound</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editContactSummary">Summary *</Label>
                <Textarea
                  id="editContactSummary"
                  value={editingContactLog.summary}
                  onChange={(e) => setEditingContactLog({...editingContactLog, summary: e.target.value})}
                  placeholder="Brief summary of the contact..."
                  rows={4}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingContactLog(null); setEditingContactLogIndex(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (viewingLead && editingContactLogIndex !== null) {
                  updateContactLogMutation.mutate({
                    leadId: viewingLead.id || viewingLead._id,
                    logIndex: editingContactLogIndex,
                    contactLog: editingContactLog
                  });
                }
              }}
              disabled={updateContactLogMutation.isPending || !editingContactLog?.summary?.trim()}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {updateContactLogMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Contact Log Confirmation Dialog */}
      <Dialog open={deletingContactLogIndex !== null} onOpenChange={(open) => { if (!open) setDeletingContactLogIndex(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Contact Log</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this contact log? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingContactLogIndex(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => {
                if (viewingLead && deletingContactLogIndex !== null) {
                  deleteContactLogMutation.mutate({
                    leadId: viewingLead.id || viewingLead._id,
                    logIndex: deletingContactLogIndex
                  });
                  setDeletingContactLogIndex(null);
                }
              }}
              disabled={deleteContactLogMutation.isPending}
            >
              {deleteContactLogMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}